import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Replay as ReplayIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import useSWR, { mutate as globalMutate } from 'swr';
import { webhooks, type DeliveryListQuery, type SubscriptionUpsertBody } from '@/api/platform';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DataTable } from '@/components/DataTable';
import { FieldRow } from '@/components/FieldRow';
import { MetricStat } from '@/components/MetricStat';
import { TenantSelect } from '@/components/TenantSelect';
import { Pagination } from '@/components/Pagination';
import { StatusPill, webhookStatusTone } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime, utcStamp } from '@/utils/format';
import type { WebhookOutbox, WebhookOutboxStatus, WebhookSubscription } from '@/types/backend';

/**
 * Two tabs:
 *   - Deliveries     — paginated webhook_outbox view, status filter,
 *                      DLQ replay, drilldown drawer with full payload.
 *   - Subscriptions  — registered receivers per tenant. Read-only here
 *                      (PR-B); admin write surface lands in a follow-up.
 *
 * Rolling counts at the top come from a separate fetch with each status
 * pinned, so the operator can spot a DLQ spike without scrolling.
 */

type TabKey = 'deliveries' | 'subscriptions';
const PAGE_SIZE = 50;

export function WebhooksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') ?? 'deliveries') as TabKey;
  const [picked, setPicked] = useState<WebhookOutbox | null>(null);
  const [pendingReplay, setPendingReplay] = useState<WebhookOutbox | null>(null);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  };

  return (
    <PageShell
      kicker="PLATFORM · WEBHOOKS"
      title="Webhooks"
      subtitle="Outbound delivery monitor. Failed rows enter DLQ after 8 attempts (1m → 5m → 30m → 2h → 6h → 24h × 3); replay a row to fast-track another retry."
      width="full"
    >
      <DeliveryStats />

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => updateParam('tab', v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          mt: 3,
          minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Tab value="deliveries" label="Deliveries" />
        <Tab value="subscriptions" label="Subscriptions" />
      </Tabs>

      {tab === 'deliveries' ? (
        <DeliveriesTab
          tenantFilter={searchParams.get('tenantId') ?? ''}
          statusFilter={(searchParams.get('status') ?? '') as WebhookOutboxStatus | ''}
          page={Number(searchParams.get('page') ?? '0')}
          onParam={updateParam}
          onPick={(row) => setPicked(row)}
          onReplay={(row) => setPendingReplay(row)}
        />
      ) : (
        <SubscriptionsTab tenantFilter={searchParams.get('tenantId') ?? ''} onParam={updateParam} />
      )}

      <DeliveryDrawer pick={picked} onClose={() => setPicked(null)} onReplay={(row) => { setPicked(null); setPendingReplay(row); }} />

      <ConfirmDialog
        open={!!pendingReplay}
        onClose={() => setPendingReplay(null)}
        title="Replay this delivery?"
        body={
          pendingReplay ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                The row goes back to <strong>PENDING</strong> and gets a fresh <code>maxAttempts + 8</code> retry budget. The next dispatcher tick picks it up.
              </Typography>
              <Typography variant="caption" color="text.tertiary">
                {pendingReplay.eventType} · {redactMiddle(pendingReplay.idempotencyKey, 6, 6)} · attempts {pendingReplay.attempts}/{pendingReplay.maxAttempts}
              </Typography>
            </>
          ) : null
        }
        actionLabel="Replay"
        onConfirm={async () => {
          if (!pendingReplay) return;
          await webhooks.replay(pendingReplay.id);
          globalMutate((key) => Array.isArray(key) && key[0] === 'webhook-deliveries');
        }}
      />
    </PageShell>
  );
}

function DeliveryStats() {
  const fetchCount = (status: WebhookOutboxStatus) =>
    webhooks.deliveries({ status, page: 0, size: 1 }).then((p) => p.totalElements);
  const { data: pending } = useSWR(['webhook-count', 'PENDING'], () => fetchCount('PENDING'));
  const { data: inFlight } = useSWR(['webhook-count', 'IN_FLIGHT'], () => fetchCount('IN_FLIGHT'));
  const { data: dlq } = useSWR(['webhook-count', 'DLQ'], () => fetchCount('DLQ'));
  const { data: sent } = useSWR(['webhook-count', 'SENT'], () => fetchCount('SENT'));

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
      <MetricStat label="Pending" value={pending ?? '…'} tone="info" hint="awaiting dispatcher tick" />
      <MetricStat label="In flight" value={inFlight ?? '…'} tone="warning" hint="POST in progress" />
      <MetricStat label="Sent" value={sent ?? '…'} tone="success" hint="2xx confirmed" />
      <MetricStat label="DLQ" value={dlq ?? '…'} tone={(dlq ?? 0) > 0 ? 'danger' : 'success'} hint={(dlq ?? 0) > 0 ? 'needs replay' : 'clean'} />
    </Box>
  );
}

function DeliveriesTab({
  tenantFilter,
  statusFilter,
  page,
  onParam,
  onPick,
  onReplay,
}: {
  tenantFilter: string;
  statusFilter: WebhookOutboxStatus | '';
  page: number;
  onParam: (k: string, v: string) => void;
  onPick: (row: WebhookOutbox) => void;
  onReplay: (row: WebhookOutbox) => void;
}) {
  const theme = useTheme();
  const query: DeliveryListQuery = useMemo(() => ({
    tenantId: tenantFilter || undefined,
    status: statusFilter || undefined,
    page,
    size: PAGE_SIZE,
  }), [tenantFilter, statusFilter, page]);
  const swrKey = useMemo(() => ['webhook-deliveries', query] as const, [query]);
  const { data, error, isLoading } = useSWR(swrKey, () => webhooks.deliveries(query));

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TenantSelect value={tenantFilter} onChange={(v) => onParam('tenantId', v)} />
        <TextField
          size="small"
          label="Status"
          select
          value={statusFilter}
          onChange={(e) => onParam('status', e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="PENDING">PENDING</MenuItem>
          <MenuItem value="IN_FLIGHT">IN_FLIGHT</MenuItem>
          <MenuItem value="SENT">SENT</MenuItem>
          <MenuItem value="DLQ">DLQ</MenuItem>
        </TextField>
      </Stack>

      <DataTable<WebhookOutbox>
        rows={data?.content}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{ icon: WebhookIcon, title: 'No deliveries match.', description: 'Loosen the filters.' }}
        onRowClick={onPick}
        columns={[
          {
            header: 'When',
            cell: (r) => <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>{relativeTime(r.createdAt)}</Typography>,
            width: 130,
          },
          {
            header: 'Tenant',
            cell: (r) => <Typography variant="caption">{redactMiddle(r.tenantId, 6, 6)}</Typography>,
            width: 160,
          },
          {
            header: 'Event',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.eventType}</Typography>
                <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: theme.typography.fontFamily }}>
                  {r.url}
                </Typography>
              </Stack>
            ),
          },
          {
            header: 'Status',
            cell: (r) => <StatusPill tone={webhookStatusTone(r.status)}>{r.status}</StatusPill>,
            width: 110,
          },
          {
            header: 'Attempts',
            align: 'right',
            cell: (r) => <Typography variant="caption">{r.attempts}/{r.maxAttempts}</Typography>,
            width: 100,
          },
          {
            header: 'Last response',
            cell: (r) => r.lastResponseStatus ? (
              <Typography variant="caption">{r.lastResponseStatus}</Typography>
            ) : <Typography variant="caption" color="text.tertiary">—</Typography>,
            width: 120,
          },
          {
            header: '',
            align: 'right',
            width: 120,
            cell: (r) => r.status === 'DLQ' ? (
              <Button
                size="small"
                variant="text"
                startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onReplay(r); }}
                sx={{ fontSize: 12 }}
              >
                Replay
              </Button>
            ) : null,
          },
        ]}
      />
      <Pagination page={data} onChange={(n) => onParam('page', String(n))} />
    </>
  );
}

function SubscriptionsTab({ tenantFilter, onParam }: { tenantFilter: string; onParam: (k: string, v: string) => void }) {
  const theme = useTheme();
  const toast = useToast();
  const swrKey = ['webhook-subs', tenantFilter] as const;
  const { data, error, isLoading } = useSWR(swrKey, () =>
    webhooks.subscriptions(tenantFilter ? { tenantId: tenantFilter } : undefined),
  );

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<WebhookSubscription | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WebhookSubscription | null>(null);

  const refresh = () => globalMutate((k) => Array.isArray(k) && k[0] === 'webhook-subs');

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TenantSelect value={tenantFilter} onChange={(v) => onParam('tenantId', v)} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          sx={{ ml: 'auto', bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          New subscription
        </Button>
      </Stack>
      <DataTable<WebhookSubscription>
        rows={data}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        searchable
        searchPlaceholder="tenant, url, event type…"
        paginated
        empty={{
          icon: WebhookIcon,
          title: 'No subscriptions yet.',
          description: 'Register the first webhook receiver — pick the tenant, paste the URL, set a shared secret, choose the events to deliver.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New subscription</Button>,
        }}
        columns={[
          {
            key: 'tenant',
            header: 'Tenant',
            sortable: true,
            sortValue: (r) => r.tenantId,
            filter: { type: 'select', getFilterValue: (r) => r.tenantId },
            cell: (r) => <Typography variant="caption">{redactMiddle(r.tenantId, 6, 6)}</Typography>,
            width: 160,
          },
          {
            key: 'url',
            header: 'URL',
            sortable: true,
            sortValue: (r) => r.url,
            filter: { type: 'text' },
            pinned: true,
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, wordBreak: 'break-all' }}>
                {r.url}
              </Typography>
            ),
          },
          {
            key: 'events',
            header: 'Events',
            searchValue: (r) => (r.eventTypes ?? []).join(' '),
            cell: (r) => (
              <Typography variant="caption" color="text.secondary">
                {r.eventTypes && r.eventTypes.length > 0 ? r.eventTypes.join(', ') : 'all'}
              </Typography>
            ),
          },
          {
            key: 'active',
            header: 'Active',
            sortable: true,
            sortValue: (r) => (r.active ? 1 : 0),
            filter: {
              type: 'select',
              options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
              getFilterValue: (r) => (r.active ? 'yes' : 'no'),
            },
            cell: (r) => r.active ? <StatusPill tone="success">Yes</StatusPill> : <StatusPill tone="mono">No</StatusPill>,
            width: 100,
          },
          {
            key: 'kek',
            header: 'Secret KEK',
            sortable: true,
            sortValue: (r) => r.secretId ?? '',
            filter: { type: 'select', getFilterValue: (r) => r.secretId ?? '' },
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamily, color: r.secretId === 'plaintext' ? theme.palette.status.warning : 'text.secondary' }}>
                {r.secretId ?? '—'}
              </Typography>
            ),
            width: 140,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: 200,
            pinned: true,
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Button size="small" variant="text" sx={{ fontSize: 12 }} onClick={() => setEditing(r)}>Edit</Button>
                <Button size="small" variant="text" color="error" sx={{ fontSize: 12 }} onClick={() => setPendingDelete(r)}>Delete</Button>
              </Stack>
            ),
          },
        ]}
      />

      <SubscriptionFormDialog
        open={creating || !!editing}
        initial={editing}
        defaultTenantId={tenantFilter || undefined}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete this webhook subscription?"
        body={
          pendingDelete ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tenant <strong>{redactMiddle(pendingDelete.tenantId, 6, 6)}</strong> will stop receiving outbound events at <code>{pendingDelete.url}</code>.
              </Typography>
              <Typography variant="caption" color="text.tertiary">
                In-flight deliveries already enqueued to webhook_outbox will still attempt delivery.
              </Typography>
            </>
          ) : null
        }
        actionLabel="Delete subscription"
        tone="danger"
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await webhooks.deleteSubscription(pendingDelete.id);
            toast.success('Subscription deleted');
            refresh();
          } catch (e) {
            toast.error('Delete failed', (e as Error).message);
          }
        }}
      />
    </>
  );
}

// -------------------- Subscription form dialog --------------------

interface SubscriptionForm {
  tenantId: string;
  url: string;
  secret: string;
  secretId: string;
  description: string;
  active: boolean;
  eventTypes: string[];
}

const ALL_EVENT_TYPES = [
  'abha.verified',
  'consent.granted',
  'data.fetched',
  'consent.revoked',
  'push.failed',
] as const;

function SubscriptionFormDialog({
  open,
  initial,
  defaultTenantId,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: WebhookSubscription | null;
  defaultTenantId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const isEdit = !!initial;

  const { control, handleSubmit, reset, watch, setValue, formState: { isSubmitting } } = useForm<SubscriptionForm>({
    defaultValues: blankForm(defaultTenantId),
  });

  useEffect(() => {
    if (open) reset(formFromSubscription(initial, defaultTenantId));
  }, [open, initial, defaultTenantId, reset]);

  const eventTypes = watch('eventTypes');
  const toggleEvent = (t: string) => {
    const next = eventTypes.includes(t) ? eventTypes.filter((x) => x !== t) : [...eventTypes, t];
    setValue('eventTypes', next, { shouldValidate: true });
  };

  const onSubmit = async (form: SubscriptionForm) => {
    try {
      const body: SubscriptionUpsertBody = {
        tenantId: form.tenantId.trim(),
        url: form.url.trim(),
        // Only send the secret if user filled it (allows leaving the field
        // blank on edit to keep the existing secret).
        ...(form.secret ? { secret: form.secret } : {}),
        ...(form.secretId ? { secretId: form.secretId.trim() } : {}),
        eventTypes: form.eventTypes.length > 0 ? form.eventTypes : undefined,
        active: form.active,
        description: form.description.trim() || undefined,
      };
      if (isEdit && initial) {
        await webhooks.updateSubscription(initial.id, body);
        toast.success('Subscription updated');
      } else {
        const sub = await webhooks.createSubscription(body);
        toast.success('Subscription created', sub.url);
      }
      onSaved();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      toast.error('Save failed', detail?.message ?? detail?.error ?? (e as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {isEdit ? 'Edit subscription' : 'New webhook subscription'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="sub-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="tenantId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TenantSelect
                  value={field.value}
                  onChange={field.onChange}
                  label="Tenant"
                  required
                  disabled={isEdit}
                  fullWidth
                  allowAll={false}
                  helperText={fieldState.error?.message ?? (isEdit ? 'Immutable on edit.' : 'Tenant whose events ship here.')}
                />
              )}
            />
            <Controller
              name="url"
              control={control}
              rules={{ required: true, pattern: /^https?:\/\// }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Receiver URL"
                  type="url"
                  required
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? 'POST destination. The receiver verifies HMAC + idempotency-key.'}
                />
              )}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
              <Controller
                name="secret"
                control={control}
                rules={{ required: !isEdit }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Shared secret"
                    type="password"
                    required={!isEdit}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? (isEdit ? 'Leave blank to keep the existing secret.' : 'HMAC-SHA256 key. Save it; we never show it again.')}
                  />
                )}
              />
              <Controller
                name="secretId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Secret KID (optional)"
                    fullWidth
                    helperText="Auto-generated if blank."
                  />
                )}
              />
            </Box>
            <Controller
              name="description"
              control={control}
              render={({ field }) => <TextField {...field} label="Description (optional)" fullWidth />}
            />
            <Stack spacing={1}>
              <Typography variant="overline" color="text.tertiary">Event types</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {ALL_EVENT_TYPES.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => toggleEvent(t)}
                    sx={{
                      bgcolor: eventTypes.includes(t) ? theme.palette.brand.lighter : theme.palette.surface.sunken,
                      color: eventTypes.includes(t) ? theme.palette.brand.dark : 'text.secondary',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 11,
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.tertiary">
                Empty = subscribe to all events. Matches FRD §6.3 vocabulary; receiver-side verifier should treat unknown event types as forward-compatible additions.
              </Typography>
            </Stack>
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Active</Typography>
                    <Typography variant="caption" color="text.tertiary">When off, the dispatcher skips this subscription on fan-out.</Typography>
                  </Box>
                </Stack>
              )}
            />
            <Alert severity="info" sx={{ borderRadius: theme.radius.sm + 'px' }}>
              Once saved, deliveries flow through the outbox + sliding-window retry (1m → 5m → 30m → 2h → 6h → 24h × 3 → DLQ). Dead-letter rows can be replayed from the Deliveries tab.
            </Alert>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          type="submit"
          form="sub-form"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function blankForm(defaultTenantId?: string): SubscriptionForm {
  return {
    tenantId: defaultTenantId ?? '',
    url: '',
    secret: '',
    secretId: '',
    description: '',
    active: true,
    eventTypes: [],
  };
}

function formFromSubscription(s: WebhookSubscription | null, defaultTenantId?: string): SubscriptionForm {
  if (!s) return blankForm(defaultTenantId);
  return {
    tenantId: s.tenantId,
    url: s.url,
    secret: '',                      // NEVER pre-fill — the API never returns it.
    secretId: s.secretId ?? '',
    description: s.description ?? '',
    active: s.active,
    eventTypes: s.eventTypes ?? [],
  };
}

function DeliveryDrawer({
  pick,
  onClose,
  onReplay,
}: {
  pick: WebhookOutbox | null;
  onClose: () => void;
  onReplay: (row: WebhookOutbox) => void;
}) {
  const theme = useTheme();
  return (
    <Drawer anchor="right" open={!!pick} onClose={onClose} PaperProps={{ sx: { width: { xs: '100vw', md: 600 } } }}>
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.border.subtle}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.tertiary">DELIVERY</Typography>
            <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 600 }}>
              {pick?.eventType ?? ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {pick?.status === 'DLQ' && (
              <Button size="small" variant="text" startIcon={<ReplayIcon fontSize="small" />} onClick={() => pick && onReplay(pick)}>
                Replay
              </Button>
            )}
            <IconButton onClick={onClose} aria-label="close drawer"><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        {pick && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>State</Typography>
              <FieldRow label="Status" value={<StatusPill tone={webhookStatusTone(pick.status)}>{pick.status}</StatusPill>} />
              <FieldRow label="Attempts" value={`${pick.attempts} of ${pick.maxAttempts}`} />
              <FieldRow label="Created" value={utcStamp(pick.createdAt)} />
              <FieldRow label="Last attempt" value={pick.lastAttemptAt ? utcStamp(pick.lastAttemptAt) : '—'} />
              <FieldRow label="Delivered" value={pick.deliveredAt ? utcStamp(pick.deliveredAt) : '—'} />
              <FieldRow label="Next retry" value={pick.nextRetryAt ? utcStamp(pick.nextRetryAt) : '—'} />
              <FieldRow label="Last response" value={pick.lastResponseStatus ?? '—'} />
              {pick.lastError && <FieldRow label="Last error" value={pick.lastError} />}
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>Routing</Typography>
              <FieldRow label="Tenant" value={pick.tenantId} mono />
              <FieldRow label="Subscription" value={pick.subscriptionId} mono />
              <FieldRow label="URL" value={pick.url} mono />
              <FieldRow label="Idempotency" value={pick.idempotencyKey} mono />
              <FieldRow label="Trace" value={pick.traceId ?? '—'} mono />
              <FieldRow label="Secret KEK" value={pick.secretId ?? '—'} mono />
            </Box>
            <Box>
              <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>Payload</Typography>
              <Box
                component="pre"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 11,
                  bgcolor: theme.palette.surface.sunken,
                  borderRadius: theme.radius.sm + 'px',
                  p: 2,
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(pick.payloadJson), null, 2);
                  } catch {
                    return pick.payloadJson;
                  }
                })()}
              </Box>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
